import { ActionIcon, Button, Flex, Menu, Stack, Title } from "@mantine/core";
import { IMinUserDepricated, IValueAndColor } from "@mctypes/global.types";
import { IIssueApi } from "@schemas/issueSchema";
import { IconLinkOff, IconLock, IconX } from "@tabler/icons-react";
import { CellEditRequestEvent, ColDef, GridApi } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { IconPlus } from "@tabler/icons-react";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { isValidDateValue } from "../../app/routes/admin/selectors";
import { useGetProjectQuery } from "../../app/projectRTK";
import { ISSUE_COMPLETED_VALUE, ISSUE_EXPIRED, ISSUE_STATES_OPTIONS } from "../../app/routes/issuesApp/issueConstants";
import { useDoUpdateIssueMutation } from "../../app/routes/issuesApp/issueRTKLegacy";
import {
  convertValueAndColorToMultiDisplayColor,
  getTaskCompleteCount,
  issueBoardHasClientAccess,
} from "../../app/routes/issuesApp/issueUtils";
import {
  addIssueIdToTableExpandedInRedux,
  removeIssueIdFromTableExpandedInRedux,
} from "../../app/routes/meeting/meetingSlice";
import { addAppModalThunk, setShowFailedMessageThunk } from "../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";
import {
  Avatar,
  ClickableTableIdButton,
  ConfirmDialog,
  DisplayDueDate,
  FailedAlert,
  OptionBadge,
  TaskCompletedCount,
} from "../index";
import { TaskForAIssueTable } from "./TaskForAIssueTable";

const getIssueBoardId = (issueBoard: IIssueApi["issueBoard"]): string => {
  return typeof issueBoard === "string" ? issueBoard : issueBoard?._id;
};

const getIssueProjectId = (issueProject: IIssueApi["project"], fallbackProjectId: string): string =>
  typeof issueProject === "string" ? issueProject : (issueProject?._id ?? fallbackProjectId);

export const ConnectedIssues: React.FC<{
  issues: IIssueApi[];
  onCreateNewIssue: () => void;
  onConnectExistingIssue?: () => void;
  onCreateNewFromDefault?: (id: string) => void;
  onDisconnectIssue: (_id: string) => void;
  isPrintMode: boolean;
  titleLabel: string;
  defaultIssueBoard?: { name: string; _id: string };
  disableCellEdit?: boolean;
  hideRegisterColumn?: boolean;
  projectId: string;
  hideAssignedTo?: boolean;
  enableReorder?: boolean;
  agendaItemId?: string;
  updateOrderOfItems?: (args: { projectId: string; newOrder: string[] }) => Promise<any> | void;
}> = ({
  issues,
  onCreateNewIssue,
  onConnectExistingIssue,
  onCreateNewFromDefault,
  onDisconnectIssue,
  isPrintMode,
  titleLabel,
  defaultIssueBoard,
  disableCellEdit,
  hideRegisterColumn = true,
  projectId,
  hideAssignedTo = false,
  enableReorder = false,
  agendaItemId,
  updateOrderOfItems,
}) => {
  const [updateIssue, { isError }] = useDoUpdateIssueMutation();

  const issueIdsTableExpandeded = useAppSelector((state) => state.meetingReducer.issueIdsTableExpandeded);
  const { data: project, isLoading: projectLoading } = useGetProjectQuery(projectId);
  const [gridApi, setGridApi] = useState<GridApi>(null);

  const onGridReady = (params) => {
    params.api.autoSizeColumns(["id", "dates.due", "status"]);
    setGridApi(params.api);
  };

  useEffect(() => {
    if (gridApi != null) {
      issues.forEach((issue) => {
        const isExpanded = issueIdsTableExpandeded.includes(issue._id);
        const hasConnectedTasks = (issue.connectedTasks?.length ?? 0) > 0;
        if (isExpanded === true && hasConnectedTasks === true) {
          gridApi.getRowNode(issue._id).setExpanded(true);
        } else {
          gridApi.getRowNode(issue._id).setExpanded(false);
        }
      });
    }
  }, [issueIdsTableExpandeded, gridApi]);

  const dispatch = useAppDispatch();

  const toggleExpand = (_id: string) => {
    const idExist = issueIdsTableExpandeded.includes(_id);
    if (idExist === true) {
      dispatch(removeIssueIdFromTableExpandedInRedux(_id));
    } else {
      dispatch(addIssueIdToTableExpandedInRedux(_id));
    }
  };

  const onRowDragEnd = async (event) => {
    if (!enableReorder) return;

    // Get the new order of issue IDs
    const newOrder = event.api.getRenderedNodes().map((node) => node.data._id); // assuming _id is the unique identifier

    try {
      await updateOrderOfItems({
        projectId: projectId,
        newOrder: newOrder,
      });
    } catch (error) {
      console.error(error);
      dispatch(setShowFailedMessageThunk("Feil ved endring av rekkefølge på tilkoblede saker"));
    }
  };

  const columnDefs: ColDef<IIssueApi, any>[] = [
    {
      hide: isPrintMode,
      rowDrag: enableReorder,
      width: 60,
      cellClass: "px-1",
      editable: false,
      suppressAutoSize: true,
      cellRenderer: (params) => (
        <ConfirmDialog
          title="Fjerne forbindelsen?"
          message="Er du sikker på at du vil fjerne koblingen til denne saken?"
          confirmCb={() => onDisconnectIssue(params.data._id)}
        >
          <ActionIcon size="xs" variant="subtle" color="warmgray.8" aria-label="Fjern kobling">
            <IconLinkOff />
          </ActionIcon>
        </ConfirmDialog>
      ),
    },
    {
      field: "id",
      editable: false,
      sortable: false,
      autoHeight: false,
      headerName: "ID",
      cellClass: "px-0",
      minWidth: 140,
      cellRenderer: (props) => {
        const { node, data } = props;
        const hasConnectedTasks = (data?.connectedTasks?.length ?? 0) > 0;
        const showClientLock =
          issueBoardHasClientAccess(typeof data?.issueBoard === "string" ? null : data?.issueBoard) &&
          data?.clientAccess !== true;
        return (
          <div className="d-flex align-items-center w-100">
            <div style={{ width: 40, flex: "0 0 40px" }}>
              {hasConnectedTasks === true && (
                <div
                  onClick={() => toggleExpand(data._id)}
                  className={`expandIcon${node.expanded === true ? " rotateIcon" : ""}`}
                >
                  <i className="fa fa-angle-down"></i>
                </div>
              )}
            </div>
            <div style={{ flex: "0 0 auto" }}>
              <ClickableTableIdButton
                id={data?.id}
                _id={data?._id}
                setItem={() =>
                  dispatch(
                    addAppModalThunk({
                      itemId: data?._id,
                      boardId: data?.issueBoard?._id ? data?.issueBoard?._id : data?.issueBoard,
                      projectId: project?._id,
                      app: AppModalEnums.ISSUE,
                    }),
                  )
                }
              />
            </div>
            {showClientLock && (
              <IconLock color="var(--mantine-color-red-6)" size={16} style={{ marginLeft: 4, marginRight: 4 }} />
            )}
            <div className="flex-fill" />
            <TaskCompletedCount taskCompleteCount={getTaskCompleteCount(data, true)} />
          </div>
        );
      },
    },
    {
      field: "title",
      headerName: "Tittel",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "assignedTo",
      headerName: "Ansv. for utførelse",
      cellEditor: "agRichSelectCellEditor",
      hide: hideAssignedTo === true,
      cellEditorParams: {
        values: project?.members.map((m) => m.user),
        // cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
        allowTyping: true,
        filterList: true,
        formatValue: (value) => value?.name ?? "",
      },
      cellRenderer: (params) => (
        <UserCellRenderer
          user={params.value}
          disableClear={disableCellEdit === true}
          onClear={() => {
            if (params.data == null) {
              return;
            }

            updateIssue({
              projectId: getIssueProjectId(params.data.project, projectId),
              issueId: params.data._id,
              attr: "assignedTo",
              value: null,
              oldValue: params.value?.name ?? "",
              issueBoardId: getIssueBoardId(params.data.issueBoard),
            });
          }}
        />
      ),
    },
    {
      field: "dates.due",
      cellEditor: "agDateCellEditor",
      headerName: "Tidsfrist",
      valueGetter: (params) => {
        return params.data?.dates?.due == null ? null : new Date(params.data.dates.due);
      },
      valueSetter: (params) => {
        return params.newValue.toISOString();
      },
      cellRenderer: (params) => {
        if (isValidDateValue(params?.value) === false) {
          return "-";
        }
        return (
          <DisplayDueDate
            dueDate={params?.value}
            isComplete={
              params.data != null &&
              (params.data.status === ISSUE_COMPLETED_VALUE || params.data.status === ISSUE_EXPIRED)
            }
          />
        );
      },
    },
    {
      field: "status",
      valueGetter: (params) => {
        const status = ISSUE_STATES_OPTIONS.find((opt) => opt.value === params.data.status);
        return status;
      },
      valueSetter: (params) => {
        return params.newValue.value;
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ISSUE_STATES_OPTIONS,
        cellRenderer: StatusBadgeRenderer,
        valueListMaxHeight: 220,
      },
      headerName: "Status",
      width: 120,
      cellRenderer: StatusBadgeRenderer,
    },
    // {
    //   field: "issueBoardTitle",
    //   headerName: "Register",
    //   hide: hideRegisterColumn,
    // },
  ];

  const onCellEditRequest = (event: CellEditRequestEvent<IIssueApi>) => {
    const issue = event.data;
    const field = event.colDef.field;

    if (issue == null || field == null) {
      return;
    }

    let updateValue;

    if (field.startsWith("status")) {
      updateValue = event.newValue.value;
    } else if (field.startsWith("dates")) {
      if (event.newValue == null) {
        updateValue = "";
      } else {
        updateValue = event.newValue;

        if (updateValue.toISOString() === event.oldValue?.toISOString()) {
          return;
        }
      }
    } else {
      updateValue = event.newValue;
    }
    updateIssue({
      projectId: getIssueProjectId(issue.project, projectId),
      issueId: issue._id,
      attr: field,
      value: updateValue,
      oldValue: event.oldValue?.name ?? "",
      issueBoardId: getIssueBoardId(issue.issueBoard),
    });
  };

  const onRowClicked = (params) => {
    if (params.event.defaultPrevented) {
      return null;
    }
    params.api.refreshCells({ force: true });
  };

  const detailCellRenderer = ({ data, node, api }: CustomCellRendererProps) => {
    return <TaskForAIssueTable issue={data} />;
  };

  return (
    <Stack gap="xs">
      {isError === true && <FailedAlert />}
      <Flex align="center" gap="xs">
        {titleLabel && <Title order={6}>{titleLabel}</Title>}
        {isPrintMode === false && (
          <>
            {onConnectExistingIssue == null && (
              <Button size="sm" variant="subtle" onClick={onCreateNewIssue}>
                <IconPlus size={16} />
              </Button>
            )}

            {onConnectExistingIssue != null && (
              <Menu withinPortal shadow="md" position="bottom-start" zIndex={2050}>
                <Menu.Target>
                  <Button size="compact-sm" variant="outline">
                    <IconPlus size={16} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={onCreateNewIssue}>Ny</Menu.Item>
                  {defaultIssueBoard != null && (
                    <Menu.Item onClick={() => onCreateNewFromDefault && onCreateNewFromDefault(defaultIssueBoard._id)}>
                      Ny i {defaultIssueBoard.name}
                    </Menu.Item>
                  )}
                  <Menu.Item onClick={onConnectExistingIssue}>Koble eksisterende</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </>
        )}
      </Flex>
      <div className="d-flex flex-column ag-theme-quartz">
        <AgGridReact
          defaultColDef={{
            sortable: true,
            suppressHeaderMenuButton: true,
            editable: !disableCellEdit,
            cellStyle: {
              whiteSpace: "normal",
              lineHeight: "1.5",
              wordBreak: "break-word",
            },
            autoHeight: true,
          }}
          domLayout={"autoHeight"}
          columnDefs={columnDefs}
          getRowId={(p) => p.data._id}
          rowData={issues}
          stopEditingWhenCellsLoseFocus={true}
          animateRows={false}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          suppressContextMenu={true}
          masterDetail={true}
          detailRowAutoHeight={true}
          onRowClicked={onRowClicked}
          detailCellRenderer={detailCellRenderer}
          onGridReady={onGridReady}
          localeText={AG_GRID_LOCALE_NOR}
          rowHeight={45}
          popupParent={document.body}
          rowDragManaged={enableReorder}
          onRowDragEnd={onRowDragEnd}
        />
      </div>
    </Stack>
  );
};

export const StatusBadgeRenderer = (params) => {
  return (
    <OptionBadge
      noWrap={true}
      activeOption={(params?.value as IValueAndColor)?.value}
      options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
    />
  );
};

export const UserCellRenderer = ({
  user,
  height = "15px",
  onClear,
  disableClear = false,
}: {
  user: IMinUserDepricated;
  height?: string;
  onClear?: () => void;
  disableClear?: boolean;
}) => {
  return user != null ? (
    <Flex align="center">
      <Avatar size={height} id={user?._id} name={user?.name} />
      <div>{user?.name}</div>
      {onClear != null && disableClear !== true && (
        <ActionIcon size="xxs" variant="subtle" color="warmgray.8" p={2} mt={2} ml={4} onClick={onClear}>
          <IconX size={14} />
        </ActionIcon>
      )}
    </Flex>
  ) : (
    <></>
  );
};
